// src/pages/admin/PanchayathProfilePage.jsx

import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  Alert,
  Button,
  Card,
  Col,
  Row,
  Spinner,
  Tab,
  Table,
  Tabs,
} from "react-bootstrap";

import {
  ArrowLeft,
  Briefcase,
  CheckCircle,
  CreditCard,
  Droplet,
  Edit2,
  FileText,
  Home,
  MapPin,
  MessageSquare,
  RefreshCcw,
  Trash2,
  Users,
} from "react-feather";

import {
  useDispatch,
  useSelector,
} from "react-redux";

import {
  useNavigate,
  useParams,
} from "react-router-dom";

import {
  fetchPanchayathProfileThunk,
} from "../../../store/slices/panchayathMasterSlice";

import "../../../css/master-common.css";


// ============================================================
// HELPERS
// ============================================================

const money = (value) => {
  return new Intl.NumberFormat(
    "en-IN",
    {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 2,
    }
  ).format(
    Number(value || 0)
  );
};

const number = (value) => {
  return new Intl.NumberFormat(
    "en-IN"
  ).format(
    Number(value || 0)
  );
};

const labelize = (value) => {
  if (
    value === null ||
    value === undefined ||
    value === ""
  ) {
    return "-";
  }

  return String(value)
    .replaceAll(
      "_",
      " "
    )
    .replace(
      /\b\w/g,
      (c) =>
        c.toUpperCase()
    );
};

const Value = ({
  label,
  value,
}) => (
  <div className="pp-field">
    <span className="pp-field-label">
      {label}
    </span>

    <strong className="pp-field-value">
      {value === null ||
      value === undefined ||
      value === ""
        ? "-"
        : value}
    </strong>
  </div>
);

const StatCard = ({
  icon,
  label,
  value,
  subtext,
}) => (
  <div className="pp-stat-card">
    <div className="pp-stat-icon">
      {icon}
    </div>

    <div className="pp-stat-content">
      <span>
        {label}
      </span>

      <strong>
        {value}
      </strong>

      {subtext ? (
        <small>
          {subtext}
        </small>
      ) : null}
    </div>
  </div>
);

// ============================================================
// COMPONENT
// ============================================================

const PanchayathProfilePage =
  () => {
    const dispatch =
      useDispatch();

    const navigate =
      useNavigate();

    const {
      pan_id,
    } = useParams();

    const [
      activeTab,
      setActiveTab,
    ] = useState(
      "overview"
    );

    const {
      panchayathProfile:
        profile,

      profileLoading:
        loading,

      error,
    } = useSelector(
      (state) =>
        state.panchayathMaster ||
        {}
    );

    // ========================================================
    // LOAD PROFILE
    // ========================================================

    useEffect(() => {
      if (pan_id) {
        dispatch(
          fetchPanchayathProfileThunk(
            pan_id
          )
        );
      }
    }, [
      dispatch,
      pan_id,
    ]);

    const panchayath =
      profile?.panchayath ||
      null;

    const overview =
      profile?.overview ||
      {};

    const administration =
      profile?.administration ||
      {};

    const houses =
      profile?.houses ||
      {};

    const water =
      profile?.water ||
      {};

    const slrm =
      profile?.slrm ||
      {};

    const collections =
      profile?.collections ||
      {};

    const waste =
      profile?.waste ||
      {};

    const subscription =
      profile?.subscription ||
      {};

    const sms =
      profile?.sms ||
      {};

    const statusActive =
      Number(
        panchayath?.status
      ) === 1 ||
      String(
        panchayath?.status ||
          ""
      ).toLowerCase() ===
        "active";

    // ========================================================
    // QUICK STATS
    // ========================================================

    const quickStats =
      useMemo(
        () => [
          {
            label:
              "Total Houses",

            value:
              number(
                overview.total_houses
              ),

            subtext:
              `${number(
                overview.active_houses
              )} active`,

            icon:
              <Home
                size={20}
              />,
          },

          {
            label:
              "Water Connections",

            value:
              number(
                overview.water_connections
              ),

            subtext:
              `${number(
                overview.pending_water_houses
              )} pending billing`,

            icon:
              <Droplet
                size={20}
              />,
          },

          {
            label:
              "SLRM Connections",

            value:
              number(
                overview.slrm_connections
              ),

            subtext:
              `${number(
                slrm?.current_month
                  ?.houses_billed
              )} billed this month`,

            icon:
              <Trash2
                size={20}
              />,
          },

          {
            label:
              "Total Wards",

            value:
              number(
                overview.total_wards
              ),

            subtext:
              "Panchayath wards",

            icon:
              <MapPin
                size={20}
              />,
          },

          {
            label:
              "Agents",

            value:
              number(
                overview.total_agents
              ),

            subtext:
              `${number(
                overview.active_agents
              )} active`,

            icon:
              <Users
                size={20}
              />,
          },

          {
            label:
              "Office Users",

            value:
              number(
                overview.total_office_users
              ),

            subtext:
              `${number(
                overview.active_office_users
              )} active`,

            icon:
              <Briefcase
                size={20}
              />,
          },

          {
            label:
              "Total Outstanding",

            value:
              money(
                overview.total_balance
              ),

            subtext:
              `Water ${money(
                overview.water_balance
              )} • SLRM ${money(
                overview.slrm_balance
              )}`,

            icon:
              <CreditCard
                size={20}
              />,
          },

          {
            label:
              "Monthly Collection",

            value:
              money(
                overview.collection_this_month
              ),

            subtext:
              `Water ${money(
                overview.water_collection_this_month
              )} • SLRM ${money(
                overview.slrm_collection_this_month
              )}`,

            icon:
              <CheckCircle
                size={20}
              />,
          },
        ],
        [
          overview,
          slrm,
        ]
      );

    // ========================================================
    // LOADING
    // ========================================================

    if (loading) {
      return (
        <div className="pp-page">
          <div className="pp-loading">
            <Spinner
              animation="border"
            />

            <span>
              Loading Panchayath profile...
            </span>
          </div>
        </div>
      );
    }

    // ========================================================
    // ERROR
    // ========================================================

    if (
      !loading &&
      (
        error ||
        !profile ||
        !panchayath
      )
    ) {
      return (
        <div className="pp-page">
          <Alert
            variant="danger"
          >
            {error ||
              "Panchayath profile not found"}
          </Alert>

          <Button
            variant="light"
            className="mm-cancel-btn"
            onClick={() =>
              navigate(
                "/settings/panchayath-master"
              )
            }
          >
            <ArrowLeft
              size={16}
            />

            Back to Panchayath Master
          </Button>
        </div>
      );
    }

    return (
      <div className="pp-page">
        {/* ==================================================
            HEADER
        ================================================== */}

        <div className="pp-header">
          <div className="pp-header-left">
            <button
              type="button"
              className="pp-back-btn"
              onClick={() =>
                navigate(
                  "/settings/panchayath-master"
                )
              }
              title="Back"
            >
              <ArrowLeft
                size={18}
              />
            </button>

            <div>
              <div className="pp-title-row">
                <h3 className="pp-title">
                  {panchayath.pan_name}
                </h3>

                <span
                  className={`pp-status ${
                    statusActive
                      ? "active"
                      : "inactive"
                  }`}
                >
                  {statusActive
                    ? "Active"
                    : "Inactive"}
                </span>
              </div>

              <div className="pp-location">
                <MapPin
                  size={14}
                />

                {panchayath.district_name ||
                  "-"}{" "}
                •{" "}
                {panchayath.taluk_name ||
                  "-"}
              </div>
            </div>
          </div>

          <div className="pp-header-actions">
            <Button
              className="mm-cancel-btn d-flex align-items-center gap-2"
              variant="light"
              onClick={() =>
                dispatch(
                  fetchPanchayathProfileThunk(
                    pan_id
                  )
                )
              }
            >
              <RefreshCcw
                size={15}
              />

              Refresh
            </Button>

            <Button
              className="mm-cancel-btn d-flex align-items-center gap-2"
              variant="light"
              onClick={() =>
                navigate(
                  `/settings/panchayath-billing?pan_id=${panchayath.pan_id}`
                )
              }
            >
              <CreditCard
                size={15}
              />

              Billing
            </Button>

            <Button
              className="mm-primary-btn d-flex align-items-center gap-2"
              onClick={() =>
                navigate(
                  `/settings/panchayath-master/edit/${panchayath.pan_id}`
                )
              }
            >
              <Edit2
                size={15}
              />

              Edit Panchayath
            </Button>
          </div>
        </div>

        {/* ==================================================
            IDENTITY
        ================================================== */}

        <Card className="pp-identity-card">
          <Card.Body>
            <Row className="g-3">
              <Col
                xl={3}
                md={6}
              >
                <Value
                  label="Panchayath ID"
                  value={
                    panchayath.pan_id
                  }
                />
              </Col>

              <Col
                xl={3}
                md={6}
              >
                <Value
                  label="District"
                  value={
                    panchayath.district_name
                  }
                />
              </Col>

              <Col
                xl={3}
                md={6}
              >
                <Value
                  label="Taluk"
                  value={
                    panchayath.taluk_name
                  }
                />
              </Col>

              <Col
                xl={3}
                md={6}
              >
                <Value
                  label="Registration Date"
                  value={
                    panchayath.registration_date
                  }
                />
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* ==================================================
            QUICK STATS
        ================================================== */}

        <Row className="g-3 pp-stats-row">
          {quickStats.map(
            (item) => (
              <Col
                key={
                  item.label
                }
                xl={3}
                md={6}
              >
                <StatCard
                  {...item}
                />
              </Col>
            )
          )}
        </Row>

        {/* ==================================================
            TABS
        ================================================== */}

        <Card className="pp-tabs-card">
          <Card.Body className="p-0">
            <Tabs
              activeKey={
                activeTab
              }
              onSelect={(key) =>
                setActiveTab(
                  key ||
                    "overview"
                )
              }
              className="pp-tabs"
            >
              {/* ============================================
                  OVERVIEW
              ============================================ */}

              <Tab
                eventKey="overview"
                title="Overview"
              >
                <div className="pp-tab-body">
                  <Row className="g-3">
                    <Col
                      xl={6}
                      lg={12}
                    >
                      <Card className="pp-section-card h-100">
                        <Card.Body>
                          <div className="pp-section-title">
                            Panchayath Details
                          </div>

                          <Row className="g-3">
                            <Col
                              md={6}
                            >
                              <Value
                                label="Panchayath Name"
                                value={
                                  panchayath.pan_name
                                }
                              />
                            </Col>

                            <Col
                              md={6}
                            >
                              <Value
                                label="Status"
                                value={
                                  statusActive
                                    ? "Active"
                                    : "Inactive"
                                }
                              />
                            </Col>

                            <Col
                              md={6}
                            >
                              <Value
                                label="Tenant Domain"
                                value={
                                  panchayath.tenant_domain
                                }
                              />
                            </Col>

                            <Col
                              md={6}
                            >
                              <Value
                                label="Database"
                                value={
                                  panchayath.db_name
                                }
                              />
                            </Col>

                            <Col
                              md={6}
                            >
                              <Value
                                label="Email"
                                value={
                                  panchayath.panchayath_email_id
                                }
                              />
                            </Col>

                            <Col
                              md={6}
                            >
                              <Value
                                label="Created At"
                                value={
                                  panchayath.created_at
                                }
                              />
                            </Col>
                          </Row>
                        </Card.Body>
                      </Card>
                    </Col>

                    <Col
                      xl={6}
                      lg={12}
                    >
                      <Card className="pp-section-card h-100">
                        <Card.Body>
                          <div className="pp-section-title">
                            Officials & Contacts
                          </div>

                          <Row className="g-3">
                            <Col
                              md={6}
                            >
                              <Value
                                label="PDO"
                                value={
                                  panchayath.pdo_name
                                }
                              />

                              <small className="pp-secondary-text">
                                {panchayath.pdo_mobile_no ||
                                  ""}
                              </small>
                            </Col>

                            <Col
                              md={6}
                            >
                              <Value
                                label="DEO"
                                value={
                                  panchayath.deo_name
                                }
                              />

                              <small className="pp-secondary-text">
                                {panchayath.deo_mobile_no ||
                                  ""}
                              </small>
                            </Col>

                            <Col
                              md={6}
                            >
                              <Value
                                label="President"
                                value={
                                  panchayath.president_name
                                }
                              />

                              <small className="pp-secondary-text">
                                {panchayath.president_mobile_no ||
                                  ""}
                              </small>
                            </Col>

                            <Col
                              md={6}
                            >
                              <Value
                                label="Key Person"
                                value={
                                  panchayath.key_person_name
                                }
                              />

                              <small className="pp-secondary-text">
                                {panchayath.key_person_mobile_no ||
                                  ""}
                              </small>
                            </Col>
                          </Row>
                        </Card.Body>
                      </Card>
                    </Col>

                    <Col
                      xl={6}
                    >
                      <Card className="pp-section-card h-100">
                        <Card.Body>
                          <div className="pp-section-title">
                            Current Month Operations
                          </div>

                          <Row className="g-3">
                            <Col
                              md={6}
                            >
                              <Value
                                label="Water Bills"
                                value={
                                  number(
                                    overview.bills_this_month
                                  )
                                }
                              />
                            </Col>

                            <Col
                              md={6}
                            >
                              <Value
                                label="Houses Billed"
                                value={
                                  number(
                                    overview.houses_billed_this_month
                                  )
                                }
                              />
                            </Col>

                            <Col
                              md={6}
                            >
                              <Value
                                label="Water Demand"
                                value={
                                  money(
                                    overview.water_demand_this_month
                                  )
                                }
                              />
                            </Col>

                            <Col
                              md={6}
                            >
                              <Value
                                label="SLRM Demand"
                                value={
                                  money(
                                    overview.slrm_demand_this_month
                                  )
                                }
                              />
                            </Col>

                            <Col
                              md={6}
                            >
                              <Value
                                label="Collection"
                                value={
                                  money(
                                    overview.collection_this_month
                                  )
                                }
                              />
                            </Col>

                            <Col
                              md={6}
                            >
                              <Value
                                label="Disconnections"
                                value={
                                  number(
                                    overview.disconnections_this_month
                                  )
                                }
                              />
                            </Col>
                          </Row>
                        </Card.Body>
                      </Card>
                    </Col>

                    <Col
                      xl={6}
                    >
                      <Card className="pp-section-card h-100">
                        <Card.Body>
                          <div className="pp-section-title">
                            Company Subscription
                          </div>

                          <Row className="g-3">
                            <Col
                              md={6}
                            >
                              <Value
                                label="Subscription Type"
                                value={
                                  subscription.type
                                }
                              />
                            </Col>

                            <Col
                              md={6}
                            >
                              <Value
                                label="Subscription Amount"
                                value={
                                  money(
                                    subscription.amount
                                  )
                                }
                              />
                            </Col>

                            <Col
                              md={6}
                            >
                              <Value
                                label="Tax Type"
                                value={
                                  subscription.tax_type
                                }
                              />
                            </Col>

                            <Col
                              md={6}
                            >
                              <Value
                                label="Next Bill Start"
                                value={
                                  subscription.bill_generation_date
                                }
                              />
                            </Col>

                            <Col
                              md={6}
                            >
                              <Value
                                label="Outstanding"
                                value={
                                  money(
                                    subscription
                                      ?.billing_summary
                                      ?.outstanding_amount
                                  )
                                }
                              />
                            </Col>

                            <Col
                              md={6}
                            >
                              <Value
                                label="Received"
                                value={
                                  money(
                                    subscription
                                      ?.billing_summary
                                      ?.received_amount
                                  )
                                }
                              />
                            </Col>
                          </Row>
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>
                </div>
              </Tab>

              {/* ============================================
                  ADMINISTRATION
              ============================================ */}

              <Tab
                eventKey="administration"
                title="Administration"
              >
                <div className="pp-tab-body">
                  <Row className="g-3 mb-3">
                    <Col
                      md={4}
                    >
                      <StatCard
                        icon={
                          <Briefcase
                            size={20}
                          />
                        }
                        label="Office Users"
                        value={
                          number(
                            administration
                              ?.users_summary
                              ?.total
                          )
                        }
                        subtext={`${number(
                          administration
                            ?.users_summary
                            ?.active
                        )} active`}
                      />
                    </Col>

                    <Col
                      md={4}
                    >
                      <StatCard
                        icon={
                          <Users
                            size={20}
                          />
                        }
                        label="Agents"
                        value={
                          number(
                            administration
                              ?.agents_summary
                              ?.total
                          )
                        }
                        subtext={`${number(
                          administration
                            ?.agents_summary
                            ?.active
                        )} active`}
                      />
                    </Col>

                    <Col
                      md={4}
                    >
                      <StatCard
                        icon={
                          <Briefcase
                            size={20}
                          />
                        }
                        label="Sales Executive"
                        value={
                          panchayath.sales_executive_name ||
                          "-"
                        }
                        subtext={
                          panchayath.sales_executive_emp_code ||
                          ""
                        }
                      />
                    </Col>
                  </Row>

                  <Card className="pp-section-card mb-3">
                    <Card.Body>
                      <div className="pp-section-title">
                        Office Users
                      </div>

                      <div className="pp-table-wrap">
                        <Table
                          hover
                          responsive
                          className="pp-table mb-0"
                        >
                          <thead>
                            <tr>
                              <th>
                                Sl. No.
                              </th>

                              <th>
                                Username
                              </th>

                              <th>
                                Role
                              </th>

                              <th>
                                Status
                              </th>
                            </tr>
                          </thead>

                          <tbody>
                            {administration
                              ?.office_users
                              ?.length ? (
                              administration.office_users.map(
                                (
                                  item,
                                  index
                                ) => (
                                  <tr
                                    key={
                                      item.uid
                                    }
                                  >
                                    <td>
                                      {index +
                                        1}
                                    </td>

                                    <td>
                                      {item.username ||
                                        "-"}
                                    </td>

                                    <td>
                                      {labelize(
                                        item.role
                                      )}
                                    </td>

                                    <td>
                                      {labelize(
                                        item.status
                                      )}
                                    </td>
                                  </tr>
                                )
                              )
                            ) : (
                              <tr>
                                <td
                                  colSpan={4}
                                  className="pp-empty"
                                >
                                  No office users found.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </Table>
                      </div>
                    </Card.Body>
                  </Card>

                  <Card className="pp-section-card">
                    <Card.Body>
                      <div className="pp-section-title">
                        Agents
                      </div>

                      <div className="pp-table-wrap">
                        <Table
                          hover
                          responsive
                          className="pp-table mb-0"
                        >
                          <thead>
                            <tr>
                              <th>
                                Sl. No.
                              </th>

                              <th>
                                Agent
                              </th>

                              <th>
                                Mobile
                              </th>

                              <th>
                                Status
                              </th>

                              <th>
                                Water
                              </th>

                              <th>
                                SLRM
                              </th>

                              <th>
                                Income
                              </th>

                              <th>
                                Expense
                              </th>
                            </tr>
                          </thead>

                          <tbody>
                            {administration
                              ?.agents
                              ?.length ? (
                              administration.agents.map(
                                (
                                  item,
                                  index
                                ) => (
                                  <tr
                                    key={
                                      item.agent_id
                                    }
                                  >
                                    <td>
                                      {index +
                                        1}
                                    </td>

                                    <td>
                                      {item.ag_name ||
                                        "-"}
                                    </td>

                                    <td>
                                      {item.mobile ||
                                        "-"}
                                    </td>

                                    <td>
                                      {labelize(
                                        item.status
                                      )}
                                    </td>

                                    <td>
                                      {labelize(
                                        item.water_access
                                      )}
                                    </td>

                                    <td>
                                      {labelize(
                                        item.slrm_access
                                      )}
                                    </td>

                                    <td>
                                      {labelize(
                                        item.income_access
                                      )}
                                    </td>

                                    <td>
                                      {labelize(
                                        item.expense_access
                                      )}
                                    </td>
                                  </tr>
                                )
                              )
                            ) : (
                              <tr>
                                <td
                                  colSpan={8}
                                  className="pp-empty"
                                >
                                  No agents found.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </Table>
                      </div>
                    </Card.Body>
                  </Card>
                </div>
              </Tab>

              {/* ============================================
                  HOUSES & WARDS
              ============================================ */}

              <Tab
                eventKey="houses"
                title="Houses & Wards"
              >
                <div className="pp-tab-body">
                  <Row className="g-3 mb-3">
                    <Col
                      xl={3}
                      md={6}
                    >
                      <StatCard
                        icon={
                          <Home
                            size={20}
                          />
                        }
                        label="Total Houses"
                        value={
                          number(
                            houses
                              ?.summary
                              ?.total_houses
                          )
                        }
                        subtext={`${number(
                          houses
                            ?.summary
                            ?.active_houses
                        )} active`}
                      />
                    </Col>

                    <Col
                      xl={3}
                      md={6}
                    >
                      <StatCard
                        icon={
                          <Droplet
                            size={20}
                          />
                        }
                        label="Water Connections"
                        value={
                          number(
                            houses
                              ?.summary
                              ?.water_connections
                          )
                        }
                      />
                    </Col>

                    <Col
                      xl={3}
                      md={6}
                    >
                      <StatCard
                        icon={
                          <Trash2
                            size={20}
                          />
                        }
                        label="SLRM Connections"
                        value={
                          number(
                            houses
                              ?.summary
                              ?.slrm_connections
                          )
                        }
                      />
                    </Col>

                    <Col
                      xl={3}
                      md={6}
                    >
                      <StatCard
                        icon={
                          <CreditCard
                            size={20}
                          />
                        }
                        label="Total House Balance"
                        value={
                          money(
                            houses
                              ?.summary
                              ?.total_balance
                          )
                        }
                      />
                    </Col>
                  </Row>

                  <Card className="pp-section-card mb-3">
                    <Card.Body>
                      <div className="pp-section-title">
                        Ward Summary
                      </div>

                      <div className="pp-table-wrap">
                        <Table
                          hover
                          responsive
                          className="pp-table mb-0"
                        >
                          <thead>
                            <tr>
                              <th>
                                Ward
                              </th>

                              <th>
                                Houses
                              </th>

                              <th>
                                Water
                              </th>

                              <th>
                                SLRM
                              </th>

                              <th>
                                Water Balance
                              </th>

                              <th>
                                SLRM Balance
                              </th>

                              <th>
                                Total Balance
                              </th>
                            </tr>
                          </thead>

                          <tbody>
                            {houses
                              ?.wards
                              ?.length ? (
                              houses.wards.map(
                                (
                                  item
                                ) => (
                                  <tr
                                    key={
                                      item.ward_id
                                    }
                                  >
                                    <td>
                                      {item.ward_name ||
                                        "-"}
                                    </td>

                                    <td>
                                      {number(
                                        item.total_houses
                                      )}
                                    </td>

                                    <td>
                                      {number(
                                        item.water_connections
                                      )}
                                    </td>

                                    <td>
                                      {number(
                                        item.slrm_connections
                                      )}
                                    </td>

                                    <td>
                                      {money(
                                        item.water_balance
                                      )}
                                    </td>

                                    <td>
                                      {money(
                                        item.slrm_balance
                                      )}
                                    </td>

                                    <td>
                                      {money(
                                        item.total_balance
                                      )}
                                    </td>
                                  </tr>
                                )
                              )
                            ) : (
                              <tr>
                                <td
                                  colSpan={7}
                                  className="pp-empty"
                                >
                                  No wards found.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </Table>
                      </div>
                    </Card.Body>
                  </Card>

                  <Card className="pp-section-card">
                    <Card.Body>
                      <div className="pp-section-title">
                        Top Outstanding Houses
                      </div>

                      <div className="pp-table-wrap">
                        <Table
                          hover
                          responsive
                          className="pp-table mb-0"
                        >
                          <thead>
                            <tr>
                              <th>
                                Door No.
                              </th>

                              <th>
                                Family Head
                              </th>

                              <th>
                                Ward
                              </th>

                              <th>
                                Mobile
                              </th>

                              <th>
                                Water
                              </th>

                              <th>
                                SLRM
                              </th>

                              <th>
                                Total
                              </th>
                            </tr>
                          </thead>

                          <tbody>
                            {houses
                              ?.top_outstanding
                              ?.length ? (
                              houses.top_outstanding.map(
                                (
                                  item
                                ) => (
                                  <tr
                                    key={
                                      item.home_id
                                    }
                                  >
                                    <td>
                                      {item.door_no ||
                                        "-"}
                                    </td>

                                    <td>
                                      {item.family_head ||
                                        "-"}
                                    </td>

                                    <td>
                                      {item.ward_name ||
                                        "-"}
                                    </td>

                                    <td>
                                      {item.mobile ||
                                        "-"}
                                    </td>

                                    <td>
                                      {money(
                                        item.water_balance
                                      )}
                                    </td>

                                    <td>
                                      {money(
                                        item.slrm_bal
                                      )}
                                    </td>

                                    <td>
                                      <strong>
                                        {money(
                                          item.balance
                                        )}
                                      </strong>
                                    </td>
                                  </tr>
                                )
                              )
                            ) : (
                              <tr>
                                <td
                                  colSpan={7}
                                  className="pp-empty"
                                >
                                  No outstanding houses found.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </Table>
                      </div>
                    </Card.Body>
                  </Card>
                </div>
              </Tab>

              {/* ============================================
                  WATER
              ============================================ */}

              <Tab
                eventKey="water"
                title="Water"
              >
                <div className="pp-tab-body">
                  <Row className="g-3 mb-3">
                    <Col
                      xl={3}
                      md={6}
                    >
                      <StatCard
                        icon={
                          <FileText
                            size={20}
                          />
                        }
                        label="Total Water Bills"
                        value={
                          number(
                            water
                              ?.summary
                              ?.total_water_bills
                          )
                        }
                      />
                    </Col>

                    <Col
                      xl={3}
                      md={6}
                    >
                      <StatCard
                        icon={
                          <Droplet
                            size={20}
                          />
                        }
                        label="Total Units"
                        value={
                          number(
                            water
                              ?.summary
                              ?.total_units
                          )
                        }
                      />
                    </Col>

                    <Col
                      xl={3}
                      md={6}
                    >
                      <StatCard
                        icon={
                          <CreditCard
                            size={20}
                          />
                        }
                        label="Total Demand"
                        value={
                          money(
                            water
                              ?.summary
                              ?.total_water_demand
                          )
                        }
                      />
                    </Col>

                    <Col
                      xl={3}
                      md={6}
                    >
                      <StatCard
                        icon={
                          <CreditCard
                            size={20}
                          />
                        }
                        label="Old Due"
                        value={
                          money(
                            water
                              ?.summary
                              ?.total_old_due
                          )
                        }
                      />
                    </Col>
                  </Row>

                  <Card className="pp-section-card mb-3">
                    <Card.Body>
                      <div className="pp-section-title">
                        Current Month
                      </div>

                      <Row className="g-3">
                        <Col
                          md={2}
                        >
                          <Value
                            label="Bills"
                            value={
                              number(
                                water
                                  ?.current_month
                                  ?.bills
                              )
                            }
                          />
                        </Col>

                        <Col
                          md={2}
                        >
                          <Value
                            label="Houses Billed"
                            value={
                              number(
                                water
                                  ?.current_month
                                  ?.houses_billed
                              )
                            }
                          />
                        </Col>

                        <Col
                          md={2}
                        >
                          <Value
                            label="Pending Houses"
                            value={
                              number(
                                water
                                  ?.current_month
                                  ?.pending_houses
                              )
                            }
                          />
                        </Col>

                        <Col
                          md={2}
                        >
                          <Value
                            label="Demand"
                            value={
                              money(
                                water
                                  ?.current_month
                                  ?.demand
                              )
                            }
                          />
                        </Col>

                        <Col
                          md={2}
                        >
                          <Value
                            label="Units"
                            value={
                              number(
                                water
                                  ?.current_month
                                  ?.units
                              )
                            }
                          />
                        </Col>

                        <Col
                          md={2}
                        >
                          <Value
                            label="Disconnections"
                            value={
                              number(
                                water
                                  ?.current_month
                                  ?.disconnections
                              )
                            }
                          />
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>

                  <Card className="pp-section-card">
                    <Card.Body>
                      <div className="pp-section-title">
                        Recent Water Bills
                      </div>

                      <div className="pp-table-wrap">
                        <Table
                          hover
                          responsive
                          className="pp-table mb-0"
                        >
                          <thead>
                            <tr>
                              <th>
                                Date
                              </th>

                              <th>
                                Door
                              </th>

                              <th>
                                Family Head
                              </th>

                              <th>
                                Ward
                              </th>

                              <th>
                                Old
                              </th>

                              <th>
                                New
                              </th>

                              <th>
                                Units
                              </th>

                              <th>
                                Amount
                              </th>
                            </tr>
                          </thead>

                          <tbody>
                            {water
                              ?.recent_bills
                              ?.length ? (
                              water.recent_bills.map(
                                (
                                  item
                                ) => (
                                  <tr
                                    key={
                                      item.wb_id
                                    }
                                  >
                                    <td>
                                      {item.bill_date ||
                                        "-"}
                                    </td>

                                    <td>
                                      {item.door_no ||
                                        "-"}
                                    </td>

                                    <td>
                                      {item.family_head ||
                                        "-"}
                                    </td>

                                    <td>
                                      {item.ward_name ||
                                        "-"}
                                    </td>

                                    <td>
                                      {item.old_reading ??
                                        "-"}
                                    </td>

                                    <td>
                                      {item.new_reading ??
                                        "-"}
                                    </td>

                                    <td>
                                      {number(
                                        item.total_units
                                      )}
                                    </td>

                                    <td>
                                      {money(
                                        item.net_total ??
                                          item.water_total_amount
                                      )}
                                    </td>
                                  </tr>
                                )
                              )
                            ) : (
                              <tr>
                                <td
                                  colSpan={8}
                                  className="pp-empty"
                                >
                                  No recent water bills found.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </Table>
                      </div>
                    </Card.Body>
                  </Card>
                </div>
              </Tab>

              {/* ============================================
                  SLRM
              ============================================ */}

              <Tab
                eventKey="slrm"
                title="SLRM"
              >
                <div className="pp-tab-body">
                  <Row className="g-3 mb-3">
                    <Col
                      xl={3}
                      md={6}
                    >
                      <StatCard
                        icon={
                          <FileText
                            size={20}
                          />
                        }
                        label="Total SLRM Bills"
                        value={
                          number(
                            slrm
                              ?.summary
                              ?.total_bills
                          )
                        }
                      />
                    </Col>

                    <Col
                      xl={3}
                      md={6}
                    >
                      <StatCard
                        icon={
                          <CreditCard
                            size={20}
                          />
                        }
                        label="Bill Amount"
                        value={
                          money(
                            slrm
                              ?.summary
                              ?.bill_amount
                          )
                        }
                      />
                    </Col>

                    <Col
                      xl={3}
                      md={6}
                    >
                      <StatCard
                        icon={
                          <CreditCard
                            size={20}
                          />
                        }
                        label="Old Due"
                        value={
                          money(
                            slrm
                              ?.summary
                              ?.old_due
                          )
                        }
                      />
                    </Col>

                    <Col
                      xl={3}
                      md={6}
                    >
                      <StatCard
                        icon={
                          <CreditCard
                            size={20}
                          />
                        }
                        label="Total Demand"
                        value={
                          money(
                            slrm
                              ?.summary
                              ?.total_demand
                          )
                        }
                      />
                    </Col>
                  </Row>

                  <Card className="pp-section-card mb-3">
                    <Card.Body>
                      <div className="pp-section-title">
                        Current Month
                      </div>

                      <Row className="g-3">
                        <Col
                          md={4}
                        >
                          <Value
                            label="Bills"
                            value={
                              number(
                                slrm
                                  ?.current_month
                                  ?.bills
                              )
                            }
                          />
                        </Col>

                        <Col
                          md={4}
                        >
                          <Value
                            label="Houses Billed"
                            value={
                              number(
                                slrm
                                  ?.current_month
                                  ?.houses_billed
                              )
                            }
                          />
                        </Col>

                        <Col
                          md={4}
                        >
                          <Value
                            label="Demand"
                            value={
                              money(
                                slrm
                                  ?.current_month
                                  ?.demand
                              )
                            }
                          />
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>

                  <Card className="pp-section-card">
                    <Card.Body>
                      <div className="pp-section-title">
                        Recent SLRM Bills
                      </div>

                      <div className="pp-table-wrap">
                        <Table
                          hover
                          responsive
                          className="pp-table mb-0"
                        >
                          <thead>
                            <tr>
                              <th>
                                Date
                              </th>

                              <th>
                                Door
                              </th>

                              <th>
                                Family Head
                              </th>

                              <th>
                                Ward
                              </th>

                              <th>
                                Days
                              </th>

                              <th>
                                Bill
                              </th>

                              <th>
                                Old Due
                              </th>

                              <th>
                                Total
                              </th>
                            </tr>
                          </thead>

                          <tbody>
                            {slrm
                              ?.recent_bills
                              ?.length ? (
                              slrm.recent_bills.map(
                                (
                                  item
                                ) => (
                                  <tr
                                    key={
                                      item.sb_id
                                    }
                                  >
                                    <td>
                                      {item.bill_date ||
                                        "-"}
                                    </td>

                                    <td>
                                      {item.door_no ||
                                        "-"}
                                    </td>

                                    <td>
                                      {item.family_head ||
                                        "-"}
                                    </td>

                                    <td>
                                      {item.ward_name ||
                                        "-"}
                                    </td>

                                    <td>
                                      {number(
                                        item.s_total_days
                                      )}
                                    </td>

                                    <td>
                                      {money(
                                        item.bill_amount
                                      )}
                                    </td>

                                    <td>
                                      {money(
                                        item.old_due
                                      )}
                                    </td>

                                    <td>
                                      {money(
                                        item.total_bill
                                      )}
                                    </td>
                                  </tr>
                                )
                              )
                            ) : (
                              <tr>
                                <td
                                  colSpan={8}
                                  className="pp-empty"
                                >
                                  No recent SLRM bills found.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </Table>
                      </div>
                    </Card.Body>
                  </Card>
                </div>
              </Tab>

              {/* ============================================
                  COLLECTIONS
              ============================================ */}

              <Tab
                eventKey="collections"
                title="Collections"
              >
                <div className="pp-tab-body">
                  <Row className="g-3 mb-3">
                    <Col
                      xl={3}
                      md={6}
                    >
                      <StatCard
                        icon={
                          <CreditCard
                            size={20}
                          />
                        }
                        label="Overall Collection"
                        value={
                          money(
                            collections
                              ?.overall
                              ?.total
                          )
                        }
                        subtext={`${number(
                          collections
                            ?.overall
                            ?.total_receipts
                        )} receipts`}
                      />
                    </Col>

                    <Col
                      xl={3}
                      md={6}
                    >
                      <StatCard
                        icon={
                          <CreditCard
                            size={20}
                          />
                        }
                        label="Today"
                        value={
                          money(
                            collections
                              ?.today
                              ?.total
                          )
                        }
                        subtext={`${number(
                          collections
                            ?.today
                            ?.receipts
                        )} receipts`}
                      />
                    </Col>

                    <Col
                      xl={3}
                      md={6}
                    >
                      <StatCard
                        icon={
                          <Droplet
                            size={20}
                          />
                        }
                        label="Water Collection"
                        value={
                          money(
                            collections
                              ?.current_month
                              ?.water
                          )
                        }
                        subtext="This month"
                      />
                    </Col>

                    <Col
                      xl={3}
                      md={6}
                    >
                      <StatCard
                        icon={
                          <Trash2
                            size={20}
                          />
                        }
                        label="SLRM Collection"
                        value={
                          money(
                            collections
                              ?.current_month
                              ?.slrm
                          )
                        }
                        subtext="This month"
                      />
                    </Col>
                  </Row>

                  <Card className="pp-section-card">
                    <Card.Body>
                      <div className="pp-section-title">
                        Recent Receipts
                      </div>

                      <div className="pp-table-wrap">
                        <Table
                          hover
                          responsive
                          className="pp-table mb-0"
                        >
                          <thead>
                            <tr>
                              <th>
                                Receipt
                              </th>

                              <th>
                                Date
                              </th>

                              <th>
                                Door
                              </th>

                              <th>
                                Family Head
                              </th>

                              <th>
                                Ward
                              </th>

                              <th>
                                Amount
                              </th>

                              <th>
                                Mode
                              </th>

                              <th>
                                Collected By
                              </th>
                            </tr>
                          </thead>

                          <tbody>
                            {collections
                              ?.recent_receipts
                              ?.length ? (
                              collections.recent_receipts.map(
                                (
                                  item
                                ) => (
                                  <tr
                                    key={
                                      item.rct_no
                                    }
                                  >
                                    <td>
                                      {item.rct_no ||
                                        "-"}
                                    </td>

                                    <td>
                                      {item.receipt_date ||
                                        "-"}
                                    </td>

                                    <td>
                                      {item.door_no ||
                                        "-"}
                                    </td>

                                    <td>
                                      {item.family_head ||
                                        "-"}
                                    </td>

                                    <td>
                                      {item.ward_name ||
                                        "-"}
                                    </td>

                                    <td>
                                      {money(
                                        item.amount
                                      )}
                                    </td>

                                    <td>
                                      {labelize(
                                        item.mode
                                      )}
                                    </td>

                                    <td>
                                      {item.collected_by ||
                                        "-"}
                                    </td>
                                  </tr>
                                )
                              )
                            ) : (
                              <tr>
                                <td
                                  colSpan={8}
                                  className="pp-empty"
                                >
                                  No recent receipts found.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </Table>
                      </div>
                    </Card.Body>
                  </Card>
                </div>
              </Tab>

              {/* ============================================
                  WASTE
              ============================================ */}

              <Tab
                eventKey="waste"
                title="Waste Management"
              >
                <div className="pp-tab-body">
                  <Row className="g-3 mb-3">
                    <Col
                      xl={3}
                      md={6}
                    >
                      <StatCard
                        icon={
                          <Trash2
                            size={20}
                          />
                        }
                        label="Sale Entries"
                        value={
                          number(
                            waste
                              ?.summary
                              ?.total_entries
                          )
                        }
                      />
                    </Col>

                    <Col
                      xl={3}
                      md={6}
                    >
                      <StatCard
                        icon={
                          <Trash2
                            size={20}
                          />
                        }
                        label="Total Quantity"
                        value={
                          number(
                            waste
                              ?.summary
                              ?.total_quantity
                          )
                        }
                      />
                    </Col>

                    <Col
                      xl={3}
                      md={6}
                    >
                      <StatCard
                        icon={
                          <Trash2
                            size={20}
                          />
                        }
                        label="Dry Quantity"
                        value={
                          number(
                            waste
                              ?.summary
                              ?.dry_quantity
                          )
                        }
                      />
                    </Col>

                    <Col
                      xl={3}
                      md={6}
                    >
                      <StatCard
                        icon={
                          <Trash2
                            size={20}
                          />
                        }
                        label="Wet Quantity"
                        value={
                          number(
                            waste
                              ?.summary
                              ?.wet_quantity
                          )
                        }
                      />
                    </Col>
                  </Row>

                  <Card className="pp-section-card">
                    <Card.Body>
                      <div className="pp-section-title">
                        Recent Waste Sales
                      </div>

                      <div className="pp-table-wrap">
                        <Table
                          hover
                          responsive
                          className="pp-table mb-0"
                        >
                          <thead>
                            <tr>
                              <th>
                                Date
                              </th>

                              <th>
                                Category
                              </th>

                              <th>
                                Type
                              </th>

                              <th>
                                Quantity
                              </th>

                              <th>
                                Added By
                              </th>
                            </tr>
                          </thead>

                          <tbody>
                            {waste
                              ?.recent_sales
                              ?.length ? (
                              waste.recent_sales.map(
                                (
                                  item
                                ) => (
                                  <tr
                                    key={
                                      item.sale_id
                                    }
                                  >
                                    <td>
                                      {item.s_date ||
                                        "-"}
                                    </td>

                                    <td>
                                      {labelize(
                                        item.waste_cat
                                      )}
                                    </td>

                                    <td>
                                      {labelize(
                                        item.waste_type
                                      )}
                                    </td>

                                    <td>
                                      {number(
                                        item.quantity
                                      )}
                                    </td>

                                    <td>
                                      {item.added_by ||
                                        "-"}
                                    </td>
                                  </tr>
                                )
                              )
                            ) : (
                              <tr>
                                <td
                                  colSpan={5}
                                  className="pp-empty"
                                >
                                  No waste sale records found.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </Table>
                      </div>
                    </Card.Body>
                  </Card>
                </div>
              </Tab>

              {/* ============================================
                  SUBSCRIPTION
              ============================================ */}

              <Tab
                eventKey="subscription"
                title="Subscription"
              >
                <div className="pp-tab-body">
                  <Card className="pp-section-card mb-3">
                    <Card.Body>
                      <div className="pp-section-title">
                        Subscription Details
                      </div>

                      <Row className="g-3">
                        <Col
                          md={3}
                        >
                          <Value
                            label="Type"
                            value={
                              subscription.type
                            }
                          />
                        </Col>

                        <Col
                          md={3}
                        >
                          <Value
                            label="Amount"
                            value={
                              money(
                                subscription.amount
                              )
                            }
                          />
                        </Col>

                        <Col
                          md={3}
                        >
                          <Value
                            label="Tax Type"
                            value={
                              subscription.tax_type
                            }
                          />
                        </Col>

                        <Col
                          md={3}
                        >
                          <Value
                            label="Registration Date"
                            value={
                              subscription.registration_date
                            }
                          />
                        </Col>

                        <Col
                          md={3}
                        >
                          <Value
                            label="Bill Generation Date"
                            value={
                              subscription.bill_generation_date
                            }
                          />
                        </Col>

                        <Col
                          md={3}
                        >
                          <Value
                            label="Total Bills"
                            value={
                              number(
                                subscription
                                  ?.billing_summary
                                  ?.total_bills
                              )
                            }
                          />
                        </Col>

                        <Col
                          md={3}
                        >
                          <Value
                            label="Received"
                            value={
                              money(
                                subscription
                                  ?.billing_summary
                                  ?.received_amount
                              )
                            }
                          />
                        </Col>

                        <Col
                          md={3}
                        >
                          <Value
                            label="Outstanding"
                            value={
                              money(
                                subscription
                                  ?.billing_summary
                                  ?.outstanding_amount
                              )
                            }
                          />
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>

                  <Card className="pp-section-card mb-3">
                    <Card.Body>
                      <div className="pp-section-title">
                        Recent Company Bills
                      </div>

                      <div className="pp-table-wrap">
                        <Table
                          hover
                          responsive
                          className="pp-table mb-0"
                        >
                          <thead>
                            <tr>
                              <th>
                                Date
                              </th>

                              <th>
                                Category
                              </th>

                              <th>
                                Period
                              </th>

                              <th>
                                Bill Amount
                              </th>

                              <th>
                                Invoice
                              </th>

                              <th>
                                Received
                              </th>

                              <th>
                                Balance
                              </th>

                              <th>
                                Status
                              </th>
                            </tr>
                          </thead>

                          <tbody>
                            {subscription
                              ?.recent_bills
                              ?.length ? (
                              subscription.recent_bills.map(
                                (
                                  item
                                ) => (
                                  <tr
                                    key={
                                      item.bill_id
                                    }
                                  >
                                    <td>
                                      {item.bill_date ||
                                        "-"}
                                    </td>

                                    <td>
                                      {labelize(
                                        item.bill_category
                                      )}
                                    </td>

                                    <td>
                                      {item.billing_period ||
                                        "-"}
                                    </td>

                                    <td>
                                      {money(
                                        item.bill_amount
                                      )}
                                    </td>

                                    <td>
                                      {item.invoice_number ||
                                        "-"}
                                    </td>

                                    <td>
                                      {money(
                                        item.received_amount
                                      )}
                                    </td>

                                    <td>
                                      {money(
                                        item.balance_amount
                                      )}
                                    </td>

                                    <td>
                                      {labelize(
                                        item.status
                                      )}
                                    </td>
                                  </tr>
                                )
                              )
                            ) : (
                              <tr>
                                <td
                                  colSpan={8}
                                  className="pp-empty"
                                >
                                  No company billing records found.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </Table>
                      </div>
                    </Card.Body>
                  </Card>

                  <Card className="pp-section-card">
                    <Card.Body>
                      <div className="pp-section-title">
                        Payment Receipts
                      </div>

                      <div className="pp-table-wrap">
                        <Table
                          hover
                          responsive
                          className="pp-table mb-0"
                        >
                          <thead>
                            <tr>
                              <th>
                                Date
                              </th>

                              <th>
                                Receipt
                              </th>

                              <th>
                                Category
                              </th>

                              <th>
                                Invoice
                              </th>

                              <th>
                                Amount
                              </th>

                              <th>
                                Mode
                              </th>

                              <th>
                                Reference
                              </th>
                            </tr>
                          </thead>

                          <tbody>
                            {subscription
                              ?.receipts
                              ?.length ? (
                              subscription.receipts.map(
                                (
                                  item
                                ) => (
                                  <tr
                                    key={
                                      item.receipt_id
                                    }
                                  >
                                    <td>
                                      {item.receipt_date ||
                                        "-"}
                                    </td>

                                    <td>
                                      {item.receipt_number ||
                                        "-"}
                                    </td>

                                    <td>
                                      {labelize(
                                        item.bill_category
                                      )}
                                    </td>

                                    <td>
                                      {item.invoice_number ||
                                        "-"}
                                    </td>

                                    <td>
                                      {money(
                                        item.received_amount
                                      )}
                                    </td>

                                    <td>
                                      {labelize(
                                        item.payment_mode
                                      )}
                                    </td>

                                    <td>
                                      {item.cheque_no ||
                                        item.transaction_reference ||
                                        "-"}
                                    </td>
                                  </tr>
                                )
                              )
                            ) : (
                              <tr>
                                <td
                                  colSpan={7}
                                  className="pp-empty"
                                >
                                  No payment receipts found.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </Table>
                      </div>
                    </Card.Body>
                  </Card>
                </div>
              </Tab>

              {/* ============================================
                  SMS
              ============================================ */}

              <Tab
                eventKey="sms"
                title="SMS"
              >
                <div className="pp-tab-body">
                  <Row className="g-3 mb-3">
                    <Col
                      xl={3}
                      md={6}
                    >
                      <StatCard
                        icon={
                          <MessageSquare
                            size={20}
                          />
                        }
                        label="Total SMS"
                        value={
                          number(
                            sms
                              ?.summary
                              ?.total
                          )
                        }
                      />
                    </Col>

                    <Col
                      xl={3}
                      md={6}
                    >
                      <StatCard
                        icon={
                          <CheckCircle
                            size={20}
                          />
                        }
                        label="Sent"
                        value={
                          number(
                            sms
                              ?.summary
                              ?.sent
                          )
                        }
                      />
                    </Col>

                    <Col
                      xl={3}
                      md={6}
                    >
                      <StatCard
                        icon={
                          <MessageSquare
                            size={20}
                          />
                        }
                        label="Pending"
                        value={
                          number(
                            sms
                              ?.summary
                              ?.pending
                          )
                        }
                      />
                    </Col>

                    <Col
                      xl={3}
                      md={6}
                    >
                      <StatCard
                        icon={
                          <MessageSquare
                            size={20}
                          />
                        }
                        label="Failed"
                        value={
                          number(
                            sms
                              ?.summary
                              ?.failed
                          )
                        }
                      />
                    </Col>
                  </Row>

                  <Card className="pp-section-card mb-3">
                    <Card.Body>
                      <div className="pp-section-title">
                        SMS Settings
                      </div>

                      <Row className="g-3">
                        <Col
                          md={4}
                        >
                          <Value
                            label="Bill Generation SMS"
                            value={
                              labelize(
                                sms
                                  ?.settings
                                  ?.bill_generation_sms
                              )
                            }
                          />
                        </Col>

                        <Col
                          md={4}
                        >
                          <Value
                            label="Receipt Generation SMS"
                            value={
                              labelize(
                                sms
                                  ?.settings
                                  ?.receipt_generation_sms
                              )
                            }
                          />
                        </Col>

                        <Col
                          md={4}
                        >
                          <Value
                            label="Payment Reminder SMS"
                            value={
                              labelize(
                                sms
                                  ?.settings
                                  ?.payment_reminder_sms
                              )
                            }
                          />
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>

                  <Card className="pp-section-card">
                    <Card.Body>
                      <div className="pp-section-title">
                        Recent SMS
                      </div>

                      <div className="pp-table-wrap">
                        <Table
                          hover
                          responsive
                          className="pp-table mb-0"
                        >
                          <thead>
                            <tr>
                              <th>
                                Type
                              </th>

                              <th>
                                Mobile
                              </th>

                              <th>
                                Message
                              </th>

                              <th>
                                Status
                              </th>

                              <th>
                                Sent At
                              </th>
                            </tr>
                          </thead>

                          <tbody>
                            {sms
                              ?.recent_logs
                              ?.length ? (
                              sms.recent_logs.map(
                                (
                                  item
                                ) => (
                                  <tr
                                    key={
                                      item.sms_log_id
                                    }
                                  >
                                    <td>
                                      {labelize(
                                        item.template_type
                                      )}
                                    </td>

                                    <td>
                                      {item.mobile_no ||
                                        "-"}
                                    </td>

                                    <td className="pp-message-cell">
                                      {item.message ||
                                        "-"}
                                    </td>

                                    <td>
                                      {labelize(
                                        item.status
                                      )}
                                    </td>

                                    <td>
                                      {item.sent_at ||
                                        item.created_at ||
                                        "-"}
                                    </td>
                                  </tr>
                                )
                              )
                            ) : (
                              <tr>
                                <td
                                  colSpan={5}
                                  className="pp-empty"
                                >
                                  No SMS records found.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </Table>
                      </div>
                    </Card.Body>
                  </Card>
                </div>
              </Tab>
            </Tabs>
          </Card.Body>
        </Card>
      </div>
    );
  };

export default PanchayathProfilePage;
